/**
 * The kitchen seen from the front, wall by wall.
 *
 * A plan says where things are; an elevation says what they look like, and it is what a
 * cabinet maker actually builds from. One per segment that carries any run, with the base
 * run, the benchtop, the splashback, the overheads, the tall units and the openings all in
 * the same drawing, because that is what an elevation IS -- not one run at a time.
 *
 * WHICH WAY IS RIGHT. Standing in the room looking at the wall, the view direction is
 * `-inward`. With up being up, `cross(view, up)` works out to `+dir` -- the wall's own
 * direction. So `u` increases to the right, and a cabinet's u is exactly
 * `run.from + offsets(items)[i]`, the number the run already carries. That is worth deriving
 * once rather than guessing and mirroring the drawing.
 *
 * Coordinates are `(u, v)`: u along the wall from its start, v UP from the finished floor,
 * both in millimetres. drawing.js flips v for paper.
 *
 * Every division here is taken from build.js's own arithmetic -- the same REVEAL, the same
 * `(h - REVEAL * (n + 1)) / n` -- so the elevation and the 3D cannot drift apart.
 */
import * as room from './room.js'
import { offsets } from './layout.js'
import { REVEAL } from './build.js'
import { SIZE, OVEN_AT } from './appliance.js'

/**
 * One elevation per segment carrying a run.
 *
 * @returns {Array<{segId, title, length, height, prims}>}
 */
export function elevations(plan, maker) {
  const lim = maker.limits
  // A double oven is the same tower with a taller aperture, and one-and-a-half is a full
  // oven with a shorter one over it: the drawing has to say so, or the elevation shows one
  // oven and the kitchen has two. `stack` is the apertures bottom to top, in millimetres.
  const ovens = (maker.ovens ?? []).find((o) => o.id === plan.ovenId)?.stack ?? [SIZE.oven.h]
  const out = []
  const segs = room.segments(plan)
  for (const seg of segs) {
    const mine = plan.runs.filter((r) => r.wallId === seg.id && r.items.some((i) => !i.gap))
    if (!mine.length) continue
    const prims = []
    const L = room.length(seg)
    const isle = room.isIsland(plan, seg.id)
    const roomH = plan.roomHeight ?? 2400
    const benchTop = lim.kickHeight + lim.baseHeight + lim.benchtopThickness

    /*
     * The wall behind it, then the openings, THEN the cabinets.
     *
     * Order matters here in a way it does not elsewhere: an opening drawn after the
     * cabinets sits on top of them, and a window crossing an overhead came out as a heavy
     * rectangle over the doors with its label across a cabinet code -- true, and unreadable.
     * Behind them and in the faint layer, it reads as what it is: a hole in the wall the
     * cabinets are standing in front of.
     *
     * An island has no wall, so it gets only a floor line.
     */
    if (!isle) {
      prims.push({ k: 'poly', layer: 'faint', close: true, pts: box(0, 0, L, roomH) })
      for (const o of (plan.openings ?? []).filter((x) => x.wallId === seg.id)) {
        const sill = o.sill ?? 0, h = o.height ?? 2040
        prims.push({ k: 'poly', layer: 'faint', close: true, pts: box(o.at, sill, o.width, h) })
        // The label goes above the opening, clear of anything standing in front of it.
        prims.push({ k: 'text', layer: 'dim', size: 2.2, align: 'centre',
          at: { x: o.at + o.width / 2, y: Math.min(sill + h + 90, roomH - 60) },
          text: o.kind === 'window'
            ? `W ${Math.round(o.width)} × ${Math.round(h)}, sill ${Math.round(sill)}`
            : `D ${Math.round(o.width)} × ${Math.round(h)}` })
      }
    }
    prims.push({ k: 'line', layer: 'wall', a: { x: -150, y: 0 }, b: { x: L + 150, y: 0 } })

    for (const run of mine) {
      const at = offsets(run.items)
      const base = run.kind === 'base'
      const y0 = base ? lim.kickHeight
        : lim.kickHeight + lim.baseHeight + lim.benchtopThickness + lim.splashbackHeight

      run.items.forEach((item, i) => {
        if (item.gap) return
        const spec = maker.cabinets.find((c) => c.id === item.specId)
        const u = run.from + at[i]
        const h = spec?.h ?? (base ? lim.baseHeight : lim.wallHeight)
        const bottom = spec?.kind === 'tall' ? lim.kickHeight : y0

        prims.push({ k: 'poly', layer: 'cabinet', close: true, pts: box(u, bottom, item.w, h) })
        if (!spec) {
          // A filler is drawn and is on the schedule as FILL; without the label on the
          // drawing it is a blank panel a maker has to measure to know what it is.
          if (item.filler) {
            prims.push({ k: 'text', layer: 'text', size: 2.1, align: 'centre',
              at: { x: u + item.w / 2, y: bottom + 60 }, text: 'FILL' })
          }
          return
        }
        prims.push(...fronts(u, bottom, item.w, h, spec))
        prims.push({ k: 'text', layer: 'text', size: 2.1, align: 'centre',
          at: { x: u + item.w / 2, y: bottom + 60 }, text: spec.id.toUpperCase() })

        // The appliance in the space, at its own size, which is the thing an elevation is
        // for: a 700-deep fridge is 1750 tall and does not reach the ceiling.
        /*
         * Both of these stand on the FLOOR, not on the kick, which is where they were drawn.
         * And a dishwasher's cavity is the kick plus the base, not its own 820: drawn at 820
         * from the top of the kick it came out taller than the benchtop it goes under.
         */
        if (spec.role === 'fridge') {
          const s = SIZE.fridge
          prims.push({ k: 'poly', layer: 'fitting', close: true,
            pts: box(u + (item.w - s.w) / 2, 0, s.w, s.h) })
        }
        if (spec.role === 'dishwasher') {
          const s = SIZE.dishwasher
          prims.push({ k: 'poly', layer: 'fitting', close: true,
            pts: box(u + (item.w - s.w) / 2, 0, s.w, lim.kickHeight + lim.baseHeight) })
        }
        if (spec.role === 'oven') {
          let oy = OVEN_AT
          for (const oh of ovens) {
            prims.push({ k: 'poly', layer: 'fitting', close: true,
              pts: box(u + (item.w - SIZE.oven.w) / 2, oy, SIZE.oven.w, oh) })
            oy += oh
          }
        }
      })

      if (base) {
        const len = run.to - run.from
        // The benchtop, edge on.
        prims.push({ k: 'poly', layer: 'bench', close: true,
          pts: box(run.from, lim.kickHeight + lim.baseHeight, len, lim.benchtopThickness) })
        // The kick, set back: drawn as a line rather than a box, which is what a shadow gap is.
        prims.push({ k: 'line', layer: 'fitting',
          a: { x: run.from, y: lim.kickHeight }, b: { x: run.from + len, y: lim.kickHeight } })
        if (!isle) {
          prims.push({ k: 'poly', layer: 'faint', close: true,
            pts: box(run.from, benchTop, len, lim.splashbackHeight) })
        }
      }
    }

    // Heights up the left-hand side: kick, benchtop, splashback, overhead. The numbers a
    // maker sets out from, and the same ones build.js uses.
    const stops = [0, lim.kickHeight, lim.kickHeight + lim.baseHeight, benchTop,
      benchTop + lim.splashbackHeight, benchTop + lim.splashbackHeight + lim.wallHeight]
      .filter((v, i, a) => a.indexOf(v) === i && v <= roomH)
    prims.push(...vChain(stops, -420))

    // And the cabinet widths along the bottom.
    const baseRun = mine.find((r) => r.kind === 'base')
    if (baseRun) {
      const at = offsets(baseRun.items)
      const stopsU = [baseRun.from, ...baseRun.items.map((it, i) => baseRun.from + at[i] + it.w)]
      prims.push(...uChain(stopsU, -320))
    }

    out.push({
      segId: seg.id,
      title: `Elevation — ${wallWord(seg.id, isle)}`,
      length: L, height: roomH,
      prims,
    })
  }
  return out
}

/** A rectangle in (u, v), inset by the reveal so a run of cabinets shows its shadow gaps. */
const box = (u, v, w, h) => [
  { x: u, y: v }, { x: u + w, y: v }, { x: u + w, y: v + h }, { x: u, y: v + h },
]

/**
 * The doors and drawers on one cabinet's face.
 *
 * The same division build.js makes, so the two cannot disagree: drawers divide the height,
 * doors divide the width, and the reveal is subtracted the same way.
 */
function fronts(u, v, w, h, spec) {
  const prims = []
  if (spec.drawers > 0) {
    const each = (h - REVEAL * (spec.drawers + 1)) / spec.drawers
    for (let k = 0; k < spec.drawers; k++) {
      const y = v + REVEAL + k * (each + REVEAL)
      prims.push({ k: 'poly', layer: 'fitting', close: true, pts: box(u + REVEAL, y, w - REVEAL * 2, each) })
    }
  } else if (spec.doors > 0) {
    const each = (w - REVEAL * (spec.doors + 1)) / spec.doors
    for (let k = 0; k < spec.doors; k++) {
      const x = u + REVEAL + k * (each + REVEAL)
      prims.push({ k: 'poly', layer: 'fitting', close: true, pts: box(x, v + REVEAL, each, h - REVEAL * 2) })
    }
  }
  return prims
}

/** A vertical chain of heights, off to the left of the elevation. */
function vChain(stops, u) {
  const prims = []
  for (const v of stops) {
    prims.push({ k: 'line', layer: 'dim', a: { x: u - 90, y: v }, b: { x: u + 90, y: v } })
  }
  prims.push({ k: 'line', layer: 'dim', a: { x: u, y: stops[0] }, b: { x: u, y: stops[stops.length - 1] } })
  for (let i = 0; i < stops.length - 1; i++) {
    const mid = (stops[i] + stops[i + 1]) / 2
    if (stops[i + 1] - stops[i] < 60) continue
    prims.push({ k: 'text', layer: 'dim', size: 2.3, align: 'centre', rot: -90,
      at: { x: u - 60, y: mid }, text: String(Math.round(stops[i + 1] - stops[i])) })
  }
  // And the overall height, which is the one number a person checks first.
  prims.push({ k: 'text', layer: 'dim', size: 2.4, align: 'centre', rot: -90,
    at: { x: u - 260, y: stops[stops.length - 1] / 2 }, text: String(Math.round(stops[stops.length - 1])) })
  return prims
}

/** A horizontal chain of widths, below the elevation. */
function uChain(stops, v) {
  const prims = []
  for (const u of stops) {
    prims.push({ k: 'line', layer: 'dim', a: { x: u, y: v - 90 }, b: { x: u, y: v + 90 } })
  }
  prims.push({ k: 'line', layer: 'dim', a: { x: stops[0], y: v }, b: { x: stops[stops.length - 1], y: v } })
  for (let i = 0; i < stops.length - 1; i++) {
    if (stops[i + 1] - stops[i] < 60) continue
    prims.push({ k: 'text', layer: 'dim', size: 2.3, align: 'centre',
      at: { x: (stops[i] + stops[i + 1]) / 2, y: v - 130 }, text: String(Math.round(stops[i + 1] - stops[i])) })
  }
  return prims
}

const wallWord = (id, isle) => isle ? 'island'
  : ({ back: 'back wall', left: 'left wall', right: 'right wall', front: 'facing wall' }[id] ?? `wall ${id}`)
